/**
 * Knowledge graph force-directed visualization.
 *
 * Custom canvas + simple physics — no d3 / cytoscape / react-force-graph.
 * Handles ~50-200 nodes smoothly. For larger graphs we'd switch to WebGL,
 * but capsules cap KG triples at small numbers (LLM extraction limit ~10/source).
 *
 * Physics: Coulomb repulsion + spring attraction along edges + gravity to
 * center + damping. Stops when total kinetic energy goes below threshold.
 *
 * Interactions: drag a node to pin it (release re-energizes), hover to
 * highlight a node and its neighbors. Wheel zooms.
 */
import { useEffect, useRef, useState } from 'react';
import type { KGTriple } from '../types';

interface Node {
  id: string;
  label: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fixed: boolean;
  degree: number;
}

interface Link {
  source: Node;
  target: Node;
  predicate: string;
}

const COLORS = {
  ink: '#050505',
  emerald: '#10B981',
  emeraldLight: '#34D399',
  paper: '#F0FDF4',
  paperDim: 'rgba(240, 253, 244, 0.55)',
  paperFaint: 'rgba(240, 253, 244, 0.25)',
  forest: '#064E3B',
  amber: '#F59E0B',
} as const;

export function KGForceGraph({ triples, height = 460 }: { triples: KGTriple[]; height?: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    if (triples.length === 0) return;

    // Build nodes + links from triples
    const nodeMap = new Map<string, Node>();
    const ensure = (id: string): Node => {
      let n = nodeMap.get(id);
      if (!n) {
        n = {
          id,
          label: id,
          x: (Math.random() - 0.5) * 200,
          y: (Math.random() - 0.5) * 200,
          vx: 0,
          vy: 0,
          fixed: false,
          degree: 0,
        };
        nodeMap.set(id, n);
      }
      return n;
    };
    const links: Link[] = triples.map((t) => {
      const s = ensure(t.subject);
      const o = ensure(t.object);
      s.degree++;
      o.degree++;
      return { source: s, target: o, predicate: t.predicate };
    });
    const nodes = Array.from(nodeMap.values());

    // Sizing
    let dpi = window.devicePixelRatio || 1;
    let width = container.clientWidth;
    let h = height;

    const resize = () => {
      width = container.clientWidth;
      h = height;
      dpi = window.devicePixelRatio || 1;
      canvas.width = width * dpi;
      canvas.height = h * dpi;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${h}px`;
    };
    resize();

    // Pan / zoom state
    let zoom = 1;
    let panX = width / 2;
    let panY = h / 2;
    setScale(zoom);

    // Drag state
    let dragNode: Node | null = null;
    let dragOffsetX = 0;
    let dragOffsetY = 0;
    let cursor: { x: number; y: number } | null = null;

    const screenToWorld = (sx: number, sy: number) => ({
      x: (sx - panX) / zoom,
      y: (sy - panY) / zoom,
    });

    const findNodeAt = (sx: number, sy: number, padding = 8): Node | null => {
      const w = screenToWorld(sx, sy);
      let best: Node | null = null;
      let bestDist = Infinity;
      for (const n of nodes) {
        const r = nodeRadius(n);
        const dx = n.x - w.x;
        const dy = n.y - w.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < r + padding && d < bestDist) {
          best = n;
          bestDist = d;
        }
      }
      return best;
    };

    const onMouseDown = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const n = findNodeAt(sx, sy);
      if (n) {
        const w = screenToWorld(sx, sy);
        dragNode = n;
        dragNode.fixed = true;
        dragOffsetX = n.x - w.x;
        dragOffsetY = n.y - w.y;
        canvas.style.cursor = 'grabbing';
      }
    };
    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      cursor = { x: sx, y: sy };
      if (dragNode) {
        const w = screenToWorld(sx, sy);
        dragNode.x = w.x + dragOffsetX;
        dragNode.y = w.y + dragOffsetY;
        dragNode.vx = dragNode.vy = 0;
      } else {
        const n = findNodeAt(sx, sy);
        setHoveredId(n?.id ?? null);
        canvas.style.cursor = n ? 'grab' : 'default';
      }
    };
    const onMouseUp = () => {
      if (dragNode) {
        dragNode.fixed = false;
        dragNode = null;
        canvas.style.cursor = 'default';
      }
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const w = screenToWorld(sx, sy);
      zoom = Math.max(0.3, Math.min(4, zoom * factor));
      panX = sx - w.x * zoom;
      panY = sy - w.y * zoom;
      setScale(zoom);
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    const resizeHandler = () => resize();
    window.addEventListener('resize', resizeHandler);

    // Force simulation parameters
    const REPULSION = 1500; // node-node Coulomb
    const SPRING_LENGTH = 110;
    const SPRING_K = 0.05;
    const GRAVITY = 0.015;
    const DAMPING = 0.86;
    const MAX_VELOCITY = 18;

    const ctx = canvas.getContext('2d')!;

    const step = () => {
      // Compute forces
      for (const a of nodes) {
        if (a.fixed) continue;
        let fx = 0, fy = 0;
        // Repulsion
        for (const b of nodes) {
          if (a === b) continue;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy + 0.01;
          const f = REPULSION / d2;
          const d = Math.sqrt(d2);
          fx += (dx / d) * f;
          fy += (dy / d) * f;
        }
        // Center gravity
        fx -= a.x * GRAVITY;
        fy -= a.y * GRAVITY;
        a.vx = (a.vx + fx) * DAMPING;
        a.vy = (a.vy + fy) * DAMPING;
      }

      // Spring along links
      for (const link of links) {
        const dx = link.target.x - link.source.x;
        const dy = link.target.y - link.source.y;
        const d = Math.sqrt(dx * dx + dy * dy) + 0.01;
        const force = (d - SPRING_LENGTH) * SPRING_K;
        const fx = (dx / d) * force;
        const fy = (dy / d) * force;
        if (!link.source.fixed) {
          link.source.vx += fx;
          link.source.vy += fy;
        }
        if (!link.target.fixed) {
          link.target.vx -= fx;
          link.target.vy -= fy;
        }
      }

      // Cap velocity + apply position
      for (const n of nodes) {
        if (n.fixed) continue;
        const v = Math.sqrt(n.vx * n.vx + n.vy * n.vy);
        if (v > MAX_VELOCITY) {
          n.vx = (n.vx / v) * MAX_VELOCITY;
          n.vy = (n.vy / v) * MAX_VELOCITY;
        }
        n.x += n.vx;
        n.y += n.vy;
      }
    };

    const draw = () => {
      ctx.save();
      ctx.scale(dpi, dpi);
      ctx.clearRect(0, 0, width, h);
      ctx.translate(panX, panY);
      ctx.scale(zoom, zoom);

      const hoveredNode = hoveredId ? nodeMap.get(hoveredId) : null;
      const isNeighbor = (id: string): boolean => {
        if (!hoveredNode) return false;
        if (hoveredNode.id === id) return true;
        return links.some((l) =>
          (l.source.id === hoveredNode.id && l.target.id === id) ||
          (l.target.id === hoveredNode.id && l.source.id === id)
        );
      };

      // Edges
      ctx.lineWidth = 1 / zoom;
      for (const link of links) {
        const highlight = hoveredNode && (link.source.id === hoveredNode.id || link.target.id === hoveredNode.id);
        ctx.strokeStyle = highlight ? COLORS.emeraldLight : 'rgba(16, 185, 129, 0.18)';
        ctx.beginPath();
        ctx.moveTo(link.source.x, link.source.y);
        ctx.lineTo(link.target.x, link.target.y);
        ctx.stroke();

        // Predicate label on hover
        if (highlight) {
          const mx = (link.source.x + link.target.x) / 2;
          const my = (link.source.y + link.target.y) / 2;
          ctx.font = `${10 / zoom}px JetBrains Mono, monospace`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const text = link.predicate;
          const padding = 4 / zoom;
          const textWidth = ctx.measureText(text).width;
          ctx.fillStyle = 'rgba(5, 5, 5, 0.85)';
          ctx.fillRect(mx - textWidth / 2 - padding, my - 7 / zoom, textWidth + padding * 2, 14 / zoom);
          ctx.fillStyle = COLORS.emeraldLight;
          ctx.fillText(text, mx, my);
        }
      }

      // Nodes
      for (const n of nodes) {
        const r = nodeRadius(n);
        const dim = hoveredNode && !isNeighbor(n.id);
        const highlight = hoveredNode && hoveredNode.id === n.id;

        // Halo for high-degree nodes (5+ connections) + heartbeat for hovered
        if (n.degree >= 5 || highlight) {
          const haloR = r + (highlight ? 8 : 4) + Math.sin(Date.now() / 600) * (highlight ? 3 : 1);
          ctx.fillStyle = `rgba(16, 185, 129, ${dim ? 0.05 : 0.18})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, haloR, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.fillStyle = highlight
          ? COLORS.emeraldLight
          : dim
          ? 'rgba(6, 78, 59, 0.5)'
          : COLORS.forest;
        ctx.strokeStyle = highlight ? COLORS.paper : COLORS.emerald;
        ctx.lineWidth = (highlight ? 2 : 1) / zoom;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Label
        ctx.font = `${highlight ? 'bold ' : ''}${11 / zoom}px Inter, sans-serif`;
        ctx.fillStyle = dim ? COLORS.paperFaint : COLORS.paper;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const lab = n.label.length > 26 ? n.label.slice(0, 24) + '…' : n.label;
        ctx.fillText(lab, n.x, n.y + r + 4 / zoom);
      }

      ctx.restore();
    };

    let raf = 0;
    const loop = () => {
      step();
      draw();
      raf = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseUp);
      canvas.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', resizeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triples, height]);

  if (triples.length === 0) {
    return (
      <div className="glass-panel rounded-xl p-8 text-center" style={{ height }}>
        <p className="label" style={{ marginBottom: 8 }}>No KG triples</p>
        <p className="text-sm" style={{ color: 'rgba(240,253,244,0.5)' }}>
          This capsule didn't extract any subject-predicate-object triples.
          Either the source had no extractable claims or the LLM run skipped it.
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="glass-panel rounded-xl overflow-hidden relative" style={{ height }}>
      <canvas ref={canvasRef} />
      <div className="absolute top-3 left-3 flex items-center gap-2 pointer-events-none">
        <span className="label" style={{ color: 'rgba(240,253,244,0.4)' }}>
          {Object.keys(triplesIndex(triples)).length} nodes · {triples.length} edges · zoom {(scale * 100).toFixed(0)}%
        </span>
      </div>
      <div className="absolute bottom-3 right-3 pointer-events-none label" style={{ color: 'rgba(240,253,244,0.3)' }}>
        drag · scroll to zoom · hover for predicate
      </div>
    </div>
  );
}

function nodeRadius(n: Node): number {
  return Math.max(5, Math.min(14, 4 + n.degree * 1.2));
}

function triplesIndex(triples: KGTriple[]): Record<string, true> {
  const out: Record<string, true> = {};
  for (const t of triples) {
    out[t.subject] = true;
    out[t.object] = true;
  }
  return out;
}
